import React, { useEffect, useRef, useState } from 'react'
import styles from './FeatureList.module.scss'
import {
  CodeEditorVisual,
  UsageVisual,
  ChatVisual,
  StorefrontVisual,
  FlowVisual,
  PipelineVisual,
} from './FeatureVisuals'

type Span = 'span-4' | 'span-3' | 'span-2' | 'span-6'
type Accent = 'blue' | 'purple' | 'warm' | 'mint' | 'plain'
type VisualKey = 'code' | 'usage' | 'chat' | 'store' | 'flow' | 'pipeline'

interface Feature {
  title: string
  description: string
  span: Span
  accent: Accent
  visual: VisualKey
}

interface FeatureData {
  tag: string
  title: string
  subTitle: string
  features: Feature[]
}

const FeatureList = () => {
  const featureData: FeatureData = {
    tag: 'Build your Business',
    title: 'Expedite your product deliverables',
    subTitle:
      'From domain expertise to building the best technical teams — see how Socommerz turns ideas into shipped products.',
    features: [
      {
        title: 'A strong technology team, on demand',
        description:
          'We stand up a full engineering team behind your vision — backed by a fractional CTO at a discounted development cost.',
        span: 'span-4',
        accent: 'blue',
        visual: 'code',
      },
      {
        title: 'Ship OpenAI wrappers, fast',
        description:
          'Production-grade wrappers powering Retail, B2B and creative product lines.',
        span: 'span-2',
        accent: 'purple',
        visual: 'usage',
      },
      {
        title: 'OpenAI as a service',
        description:
          'Conversational, AI-native commerce experiences hooked into your business plans.',
        span: 'span-2',
        accent: 'warm',
        visual: 'chat',
      },
      {
        title: 'Build retail modules',
        description:
          'Composable retail building blocks you can deploy across every channel.',
        span: 'span-2',
        accent: 'blue',
        visual: 'store',
      },
      {
        title: 'Data architecture-as-a-service',
        description:
          'Migrate legacy systems and design data flows that scale with the business.',
        span: 'span-2',
        accent: 'mint',
        visual: 'flow',
      },
      {
        title: 'Build · Operate · Transfer',
        description:
          'We build robust applications, run them smoothly, then hand over a clean, controlled codebase.',
        span: 'span-6',
        accent: 'plain',
        visual: 'pipeline',
      },
    ],
  }

  const gridRef = useRef<HTMLDivElement>(null)
  const [revealed, setRevealed] = useState(false)

  // Reveal cards (staggered) + trigger live mockups once the grid scrolls in.
  useEffect(() => {
    const node = gridRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // Cursor-following spotlight per card.
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const card = event.currentTarget
    const rect = card.getBoundingClientRect()
    card.style.setProperty('--mx', `${event.clientX - rect.left}px`)
    card.style.setProperty('--my', `${event.clientY - rect.top}px`)
  }

  const renderVisual = (key: VisualKey) => {
    switch (key) {
      case 'code':
        return <CodeEditorVisual />
      case 'usage':
        return <UsageVisual active={revealed} end={50} />
      case 'chat':
        return <ChatVisual />
      case 'store':
        return <StorefrontVisual />
      case 'flow':
        return <FlowVisual />
      case 'pipeline':
        return <PipelineVisual />
    }
  }

  return (
    <div className={styles['feature-list-wrapper']}>
      <div className={'tag'}>
        <span>{featureData.tag}</span>
      </div>
      <h2 className={styles['feature-title']}>{featureData.title}</h2>
      <div className={styles['feature-sub-title']}>{featureData.subTitle}</div>

      <div
        ref={gridRef}
        className={`${styles['bento']} ${revealed ? styles['revealed'] : ''}`}
      >
        {featureData.features.map((item, index) => (
          <article
            key={`feature-card-${index}`}
            className={`${styles['card']} ${styles[item.span]} ${
              styles[`accent-${item.accent}`]
            }`}
            onMouseMove={handleMouseMove}
            style={{ '--i': index } as React.CSSProperties}
          >
            <div className={styles['card-glow']} aria-hidden='true' />
            <header className={styles['card-head']}>
              <div>
                <h3 className={styles['card-title']}>{item.title}</h3>
                <p className={styles['card-desc']}>{item.description}</p>
              </div>
            </header>
            <div className={styles['card-stage']}>{renderVisual(item.visual)}</div>
          </article>
        ))}
      </div>
    </div>
  )
}

export default FeatureList
